import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import {
  Loader2, FileText, Download, ExternalLink, ScrollText,
  ChevronDown, ChevronRight, Users, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  active: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  fulfilled: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20',
  terminated_by_brand: 'bg-rose-500/10 text-rose-300 border-rose-500/20',
  disputed: 'bg-red-500/10 text-red-300 border-red-500/20',
};

const STATUS_LABEL = {
  active: 'Active',
  fulfilled: 'Fulfilled',
  terminated_by_brand: 'Brand passed',
  disputed: 'In mediation',
};

const fmtCurrency = (cents) =>
  ((cents || 0) / 100).toLocaleString('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  });

/**
 * Engagement Contracts library (`/contracts`).
 *
 * Two views in one page:
 *   1. Top: Cohort cards grouped by wave/campaign — each cohort shows
 *      total creators, total gross, status mix, and a click-to-expand that
 *      filters the table below to just that cohort.
 *   2. Bottom: Flat row-per-contract table with campaign + tentpole columns
 *      so every contract is mapped back to the campaign it came from.
 */
export default function ContractsPage() {
  const [items, setItems] = useState(null);
  const [cohorts, setCohorts] = useState([]);
  const [error, setError] = useState(null);
  const [activeCohortId, setActiveCohortId] = useState(null); // null = show all
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/brand/contracts?limit=500`, { withCredentials: true })
      .then(({ data }) => {
        if (data.ok === false) {
          setError('Contracts service is temporarily unavailable.');
          setItems([]);
          setCohorts([]);
        } else {
          setItems(data.contracts || []);
          setCohorts(data.cohorts || []);
        }
      })
      .catch(e => {
        setError(e?.response?.data?.detail || 'Could not load contracts');
        setItems([]);
        setCohorts([]);
      });
  }, []);

  const filteredItems = useMemo(() => {
    if (!items) return null;
    if (!activeCohortId) return items;
    return items.filter(c => c.wave_id === activeCohortId);
  }, [items, activeCohortId]);

  const summary = useMemo(() => {
    const list = items || [];
    const total = list.length;
    const fulfilled = list.filter(c => c.status === 'fulfilled').length;
    const active = list.filter(c => c.status === 'active').length;
    const grossCents = list.reduce((s, c) => s + (c.gross_cents || 0), 0);
    return { total, fulfilled, active, grossCents };
  }, [items]);

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 max-w-[1400px] mx-auto" data-testid="contracts-page">
        <div className="mb-5 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-black text-white flex items-center gap-2">
              <ScrollText className="h-6 w-6 text-[#00D4FF]" /> Engagement Contracts
            </h1>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl">
              Auto-generated and double-signed the moment a creator accepts. Grouped into cohort
              contracts per campaign — click a cohort to drill in, or browse the flat ledger below.
            </p>
          </div>
          {items && items.length > 0 && (
            <div className="flex items-center gap-3 text-[11px]">
              <Stat label="Total contracts" value={summary.total} />
              <Stat label="Fulfilled" value={summary.fulfilled} color="text-cyan-300" />
              <Stat label="Active" value={summary.active} color="text-emerald-300" />
              <Stat label="Total gross" value={fmtCurrency(summary.grossCents)} color="text-white" />
            </div>
          )}
        </div>

        {items === null && (
          <div className="flex items-center gap-3 text-sm text-gray-500" data-testid="contracts-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading contracts…
          </div>
        )}
        {error && items !== null && (
          <div className="text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4" data-testid="contracts-error">{error}</div>
        )}

        {items && items.length === 0 && !error && (
          <div className="rounded-2xl border border-dashed border-[#1f1f1f] bg-[#0A0A0A] p-12 text-center" data-testid="contracts-empty">
            <FileText className="h-10 w-10 mx-auto mb-3 text-gray-600" />
            <h3 className="text-base font-bold text-gray-300 mb-1">No engagement contracts yet.</h3>
            <p className="text-xs text-gray-500 max-w-md mx-auto">
              Each time a creator accepts a wave, a signed PDF will land here automatically.
            </p>
          </div>
        )}

        {/* ─── Cohort cards ─── */}
        {cohorts && cohorts.length > 0 && (
          <section className="mb-6" data-testid="contracts-cohorts">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-[10px] uppercase tracking-[0.22em] font-black text-[#00D4FF] flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> Cohort contracts · grouped by campaign
              </h2>
              {activeCohortId && (
                <button
                  type="button"
                  onClick={() => setActiveCohortId(null)}
                  data-testid="cohort-clear"
                  className="text-[10px] uppercase tracking-wider text-gray-500 hover:text-white transition"
                >
                  Clear filter
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {cohorts.map(c => (
                <CohortCard
                  key={c.wave_id}
                  cohort={c}
                  active={activeCohortId === c.wave_id}
                  onClick={() => setActiveCohortId(activeCohortId === c.wave_id ? null : c.wave_id)}
                />
              ))}
            </div>
          </section>
        )}

        {/* ─── Flat ledger table ─── */}
        {filteredItems && filteredItems.length > 0 && (
          <div className="rounded-2xl border border-[#1f1f1f] bg-[#121212] overflow-hidden"
               data-testid="contracts-list">
            <div className="px-4 py-3 border-b border-[#1f1f1f] flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.22em] font-black text-gray-500">
                {activeCohortId
                  ? `${filteredItems.length} contract${filteredItems.length === 1 ? '' : 's'} in this cohort`
                  : `Full ledger · ${filteredItems.length} signed contracts`}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0A0A0A] text-[10px] uppercase tracking-widest text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold">Contract</th>
                    <th className="text-left px-4 py-3 font-bold">Campaign / Tentpole</th>
                    <th className="text-left px-4 py-3 font-bold">Creator</th>
                    <th className="text-left px-4 py-3 font-bold">Gross</th>
                    <th className="text-left px-4 py-3 font-bold">State</th>
                    <th className="text-left px-4 py-3 font-bold">Status</th>
                    <th className="text-left px-4 py-3 font-bold">Signed</th>
                    <th className="text-right px-4 py-3 font-bold">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((c, idx) => (
                    <ContractRow
                      key={c.contract_id || idx}
                      contract={c}
                      isExpanded={expandedRow === c.contract_id}
                      onToggle={() => setExpandedRow(expandedRow === c.contract_id ? null : c.contract_id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}


function Stat({ label, value, color = 'text-gray-300' }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] uppercase tracking-widest text-gray-600">{label}</span>
      <span className={`text-sm font-bold ${color}`}>{value}</span>
    </div>
  );
}


function CohortCard({ cohort, active, onClick }) {
  const total = cohort.creator_count || 0;
  const fulfilled = cohort.fulfilled_count || 0;
  const fulfilledPct = total > 0 ? Math.round((fulfilled / total) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`cohort-card-${cohort.wave_id}`}
      className={`group text-left rounded-xl border bg-[#121212] hover:bg-[#161616] transition p-4 ${
        active ? 'border-[#00D4FF]/60 ring-1 ring-[#00D4FF]/30' : 'border-[#1f1f1f] hover:border-[#2a2a2a]'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#00D4FF]">
          {cohort.tentpole || 'Cohort'}
        </span>
        <span className="text-[10px] text-gray-500 inline-flex items-center gap-1">
          <Users className="h-3 w-3" />{total}
        </span>
      </div>
      <p className="text-[13px] font-semibold text-white leading-snug line-clamp-2 mb-3">
        {cohort.campaign_name || 'Untitled cohort'}
      </p>
      <div className="space-y-2">
        <div className="flex items-baseline gap-2">
          <span className="text-base font-black text-emerald-300">
            {fmtCurrency(cohort.gross_cents)}
          </span>
          <span className="text-[10px] text-gray-500">total gross</span>
        </div>
        <div>
          <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
            <span>{fulfilled} fulfilled · {cohort.active_count || 0} active</span>
            <span>{fulfilledPct}%</span>
          </div>
          <div className="h-1 rounded-full bg-[#1f1f1f] overflow-hidden">
            <div
              className="h-full rounded-full bg-cyan-400/60"
              style={{ width: `${fulfilledPct}%` }}
            />
          </div>
        </div>
        {(cohort.platforms || []).length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {cohort.platforms.map(p => (
              <span
                key={p}
                className="text-[9px] uppercase tracking-widest text-gray-500
                           border border-[#1f1f1f] rounded-full px-1.5 py-0.5"
              >
                {p}
              </span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
}


function ContractRow({ contract, isExpanded, onToggle }) {
  const status = contract.status || 'active';
  const statusCls = STATUS_COLOR[status] || STATUS_COLOR.active;
  const gross = fmtCurrency(contract.gross_cents);
  const signed = contract.creator_signed_at
    ? new Date(contract.creator_signed_at).toLocaleDateString()
    : '—';

  return (
    <>
      <tr
        className="border-t border-[#1f1f1f] hover:bg-[#0A0A0A]/50 transition cursor-pointer"
        data-testid={`contract-row-${contract.contract_id}`}
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {isExpanded
              ? <ChevronDown className="h-3 w-3 text-gray-600" />
              : <ChevronRight className="h-3 w-3 text-gray-600" />}
            <div>
              <p className="font-mono text-xs">{contract.contract_id}</p>
              <p className="text-[10px] text-gray-500">
                v{contract.version || '1.0'} · {(contract.terms_hash || '').slice(0, 8)}…
              </p>
              {contract.creator_drawn_signature_url && (
                <p className="text-[10px] text-emerald-400 mt-0.5 flex items-center gap-1"
                   data-testid={`contract-hand-signed-${contract.contract_id}`}>
                  <span className="inline-block h-1 w-1 rounded-full bg-emerald-400" />
                  Hand-signed{contract.creator_typed_full_name ? ` by ${contract.creator_typed_full_name}` : ''}
                </p>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 py-3 max-w-[260px]">
          <p className="text-[12px] font-semibold text-white truncate">
            {contract.campaign_name || 'Untitled'}
          </p>
          {contract.tentpole && (
            <p className="text-[10px] text-[#00D4FF]/80 truncate">{contract.tentpole}</p>
          )}
        </td>
        <td className="px-4 py-3">
          <p className="text-[12px] text-gray-200">{contract.creator_name || '—'}</p>
          <p className="text-[10px] text-gray-500">{contract.creator_handle}</p>
        </td>
        <td className="px-4 py-3 text-emerald-300 font-bold whitespace-nowrap">{gross}</td>
        <td className="px-4 py-3 text-gray-400 text-xs">{contract.governing_state || '—'}</td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] font-bold uppercase tracking-widest ${statusCls}`}>
            {STATUS_LABEL[status] || status}
          </span>
        </td>
        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{signed}</td>
        <td className="px-4 py-3 text-right">
          {contract.contract_url ? (
            <a
              href={contract.contract_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`contract-download-${contract.contract_id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#1f1f1f] hover:border-[#00D4FF] text-[10px] uppercase tracking-widest font-bold text-gray-300 hover:text-[#00D4FF] transition"
            >
              <Download className="h-3 w-3" /> PDF <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="text-[10px] text-gray-600">Pending</span>
          )}
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-[#0A0A0A]/60 border-t border-[#1f1f1f]" data-testid={`contract-expand-${contract.contract_id}`}>
          <td colSpan={8} className="px-6 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
              <DetailField label="Platform" value={contract.platform} />
              <DetailField label="Role" value={contract.role} />
              <DetailField label="Platform fee (8%)" value={fmtCurrency(contract.platform_fee_cents)} />
              <DetailField label="Creator net (92%)" value={fmtCurrency(contract.creator_net_cents)} />
              <DetailField label="Creator signed" value={contract.creator_signed_at ? new Date(contract.creator_signed_at).toLocaleString() : '—'} />
              <DetailField label="Brand counter-signed" value={contract.brand_signed_at ? new Date(contract.brand_signed_at).toLocaleString() : '—'} />
              <DetailField label="Wave assignment" value={contract.wave_assignment_id} mono />
              <DetailField label="Terms hash" value={contract.terms_hash} mono />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


function DetailField({ label, value, mono = false }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-widest text-gray-600 mb-0.5">{label}</p>
      <p className={`text-[11px] text-gray-200 ${mono ? 'font-mono' : ''} truncate`}>
        {value || '—'}
      </p>
    </div>
  );
}
